/**
 * `Switch` (W1-12 ui-kit-i18n; contract SwitchProps): Radix switch (role="switch") with a clickable label; Space
 * and Enter toggle it. The track shows an on/off glyph besides its colour. A disabled reason keeps it focusable
 * (aria-disabled), ignores changes and explains itself in a tooltip and through aria-describedby.
 */

import * as RSw from '@radix-ui/react-switch';
import { useId } from 'react';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import s from './Switch.module.css';
import { Tooltip } from './Tooltip';
import type { SwitchProps } from './types';

export function Switch(props: SwitchProps & { readonly className?: string | undefined }) {
  const tr = useTr();
  const id = useId();
  const reasonId = useId();
  const reason = props.disabledReason === undefined || props.disabledReason === null ? null : tr(props.disabledReason);
  const disabled = reason !== null;
  const control = (
    <RSw.Root
      id={id}
      className={s.track}
      checked={props.checked}
      aria-disabled={disabled ? true : undefined}
      aria-describedby={disabled ? reasonId : undefined}
      onCheckedChange={(c) => {
        if (!disabled) props.onChange(c);
      }}
    >
      <RSw.Thumb className={s.thumb} />
    </RSw.Root>
  );
  return (
    <div className={cx(s.row, disabled && s.disabled, props.className)}>
      <label htmlFor={id} className={s.label}>
        {props.label}
      </label>
      {reason === null ? control : <Tooltip content={reason}>{control}</Tooltip>}
      {reason !== null && (
        <span id={reasonId} hidden>
          {reason}
        </span>
      )}
    </div>
  );
}
